// ════════════════════════════════════════════════════════════════════════════
// D27 — Centralized user-friendly error messages for admin-web
// ════════════════════════════════════════════════════════════════════════════
//
// The admin-api surfaces machine-readable tokens in the error payload:
//   { success: false, error: { code: 'MFA_FAILED', message: 'TOTP_REPLAY' } }
//
// Some fields carry a semantic token as the *code* (e.g. MFA_REQUIRED), others
// carry it as the *message* (e.g. TOTP_REPLAY when the catch(err) path wraps
// `new Error('TOTP_REPLAY')`).  This module accepts EITHER and returns a
// Spanish, user-friendly string.
//
// Keep this file lean.  Only map error tokens that surface in the admin-web
// UI today.  New tokens should be added alongside the feature that produces
// them.  No i18n framework — admin console is internal-only.
// ════════════════════════════════════════════════════════════════════════════

export const errorCodeToMessage: Record<string, string> = {
  // SEC-FU-2: TOTP replay protection — same code within the 90s window
  TOTP_REPLAY:
    'Ese código TOTP ya fue usado. Espera hasta 90 segundos y usa el siguiente código de tu app autenticadora.',

  // Generic invalid TOTP (otplib verification failed)
  INVALID_TOTP: 'Código TOTP inválido. Intenta de nuevo.',

  // MFA required but not provided / session expired
  MFA_REQUIRED: 'Se requiere verificación MFA para continuar.',
  MFA_FAILED: 'Error al verificar MFA. Intenta nuevamente.',
  INVALID_MFA_SESSION: 'Sesión MFA expirada. Vuelve a iniciar sesión.',
  'Invalid MFA session': 'Sesión MFA expirada. Vuelve a iniciar sesión.',

  // Login failures
  INVALID_CREDENTIALS: 'Correo o contraseña incorrectos.',
  ACCOUNT_LOCKED:
    'Cuenta bloqueada temporalmente por múltiples intentos fallidos. Intenta más tarde.',
  ACCOUNT_DISABLED: 'La cuenta está desactivada. Contacta a un administrador.',

  // D42 (C8): self-service MFA disable. Defensive branches: MFA_NOT_ENABLED
  // should not surface in practice (the UI hides the button when disabled)
  // but we map it anyway so a stale tab shows a sensible message.
  MFA_NOT_ENABLED: 'MFA no está activo en esta cuenta.',
  RATE_LIMIT_EXCEEDED:
    'Demasiados intentos. Espera unos minutos antes de volver a intentarlo.',
  // Backend-specific rate limit code for /auth/mfa/disable (mfaDisableLimiter)
  MFA_DISABLE_RATE_LIMIT:
    'Demasiados intentos de desactivar MFA. Espera 15 minutos antes de intentar de nuevo.',

  // C9 A.6 — MFA backup codes (regenerate + disable-via-backup branch)
  BACKUP_CODE_INVALID: 'Código de backup inválido.',
  INVALID_BACKUP_CODE: 'Código de backup inválido o ya usado.',
  BACKUP_CODE_USED: 'Este código ya fue usado.',
  BACKUP_CODES_EXHAUSTED:
    'No te quedan códigos de backup. Regenera nuevos desde la página de MFA.',

  // C9 B.5 — Admin Integrations module (SUPER_ADMIN)
  INTEGRATION_NOT_FOUND: 'Integración no encontrada.',
  INTEGRATION_INVALID_KIND: 'Tipo de integración no soportado.',
  INTEGRATION_VALIDATION_ERROR:
    'El valor no cumple el formato requerido (PAT o webhook URL).',
};

/**
 * Map a machine-readable error token (from `error.code` or `error.message`)
 * to a user-friendly Spanish message.  Falls back to the provided `fallback`
 * when no entry matches, preserving whatever the backend sent so technical
 * details are never silently dropped.
 */
export function getFriendlyErrorMessage(
  token: string | undefined | null,
  fallback: string
): string {
  if (!token) return fallback;
  const hit = errorCodeToMessage[token];
  if (hit) return hit;
  // Case-insensitive fallback lookup for tokens like 'totp_replay'
  const upper = token.toUpperCase();
  if (errorCodeToMessage[upper]) return errorCodeToMessage[upper];
  return fallback;
}

/**
 * Convenience helper that inspects an Axios-ish error shape
 *   { response: { data: { error: { code, message } } } }
 * and returns the best friendly message available.  Checks `code` first,
 * then `message`, falling back to the provided default.
 */
export function friendlyFromApiError(err: unknown, fallback: string): string {
  const apiErr = (
    err as { response?: { data?: { error?: { code?: string; message?: string } } } }
  )?.response?.data?.error;
  if (!apiErr) return fallback;
  const byCode = getFriendlyErrorMessage(apiErr.code, '');
  if (byCode) return byCode;
  const byMessage = getFriendlyErrorMessage(apiErr.message, '');
  if (byMessage) return byMessage;
  return apiErr.message || apiErr.code || fallback;
}
